import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { FaroRoutes } from '@grafana/faro-react';
import { BrowserRouter, Route } from 'react-router-dom';
import {
  // DhlFooter,
  DhlLoader,
  DhlModal
} from '@dhl-official/react-library';
import { PATHS, metaData, versionData } from './constants';
import { ThemeContext } from '../../styles/ThemeContext';
import Header from '../../components/Header';
import '../../Language/i18next';
import { LoadingContext } from '../../contexts/LoadingContext';
import {
  //  NOTIFICATION_TYPES,
  NotificationContext
} from '../../contexts/NotificationContext';
import DhlSnackbar from '../../components/DhlSnackbar';
import { isEmpty, validateDHLEmailDomain } from '../../utils';
import './App.css';
import Error from '../ErrorPage';
import DhlFooter from '../../components/DhlFooter';
import ScrollToTop from '../../components/ScrollToTop';
import { JSX } from 'react/jsx-runtime';
import withAdobeAnalytics from '../../components/Adobe';
import { ThemeType } from '../../styles/types/ThemeType';
import { UserContext } from '../../contexts/UserContext';
import { device } from '../../styles/Device';
import { LazyRouteLoader, MSCRoutes } from './LazyRouteLoader';
import { useMsal } from '@azure/msal-react';
import logger from '../../utils/logger';
import { loginRequest } from '../../authConfig';
import { ensureLoginAccessTokenCookie, hasLoginAccessTokenCookie, waitForLoginAccessTokenCookie } from '../../utils/loginAccessTokenCookie';

/** sessionStorage key used to survive the MSAL redirect round-trip. */
const EXPORT_DOWNLOAD_REDIRECT_KEY = 'msc.exportDownloadReturnPath';

const isDevelopment = process.env.NODE_ENV === 'development';

const ImpersonationWrapper = styled.div<{ theme: ThemeType; impersonated: boolean }>`
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  position: absolute;
  background-color: ${props => (props.impersonated ? props.theme.color.red[500] : props.theme.secondaryBg)};
  align-items: center;
`;

const AppWrapper = styled.div<{ theme: ThemeType; impersonated: boolean }>`
  @media ${device.mobile} {
    width: ${props => (props.impersonated ? '98%' : '100%')};
  }
  @media ${device.tablet} {
    width: ${props => (props.impersonated ? '99%' : '100%')};
  }
  @media ${device.laptop} {
    width: ${props => (props.impersonated ? '99%' : '100%')};
  }
  @media ${device.desktop} {
    width: ${props => (props.impersonated ? '99.5%' : '100%')};
  }
  @media ${device.largeDesktop} {
    width: ${props => (props.impersonated ? '99.5%' : '100%')};
  }
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  height: 100%;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  position: absolute;
  background-color: ${props => props.theme.secondaryBg};
`;

const FooterWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  background-color: ${props => props.theme.color.grey[50]};
`;

const MainContentWrapper = styled.div`
  width: 100%;
  min-height: calc(100vh - 180px);
  display: flex;
  flex-direction: row;
  justify-content: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1920px;
  height: 100%;
`;

const BottomWrapper = styled.div`
  width: 100%;
  // min-height: calc(100vh - 60px);
  overflow: auto;
  display: block;
  position: relative;
  flex-direction: column;
  background-color: ${props => props.theme.secondaryBg};
`;

function App(): JSX.Element {
  const { theme } = useContext(ThemeContext);
  const { user, isLogin } = useContext(UserContext);
  const { isLoading, isUserLoading, loadings } = useContext(LoadingContext);
  const { type, message, setType, setMessage } = useContext(NotificationContext);

  const [isNotificationShowing, setIsNotificationShowing] = useState(false);

  const { accounts, inProgress, instance } = useMsal();
  const isAuthenticated = accounts.length > 0;
  const [isAuthCookieReady, setIsAuthCookieReady] = useState<boolean>(
    !isAuthenticated || hasLoginAccessTokenCookie()
  );

  useEffect(() => {
    let isCancelled = false;

    const ensureAuthCookie = async (): Promise<void> => {
      if (!isAuthenticated) {
        if (!isCancelled) {
          setIsAuthCookieReady(true);
        }
        return;
      }

      if (hasLoginAccessTokenCookie()) {
        if (!isCancelled) {
          setIsAuthCookieReady(true);
        }
        return;
      }

      if (!isCancelled) {
        setIsAuthCookieReady(false);
      }

      try {
        await ensureLoginAccessTokenCookie({
          instance,
          loginRequest,
          account: accounts[0]
        });
      } catch (error) {
        logger.auth.warn('Could not acquire token while waiting for loginAccessToken cookie', error);
      }

      const isReady = await waitForLoginAccessTokenCookie(5000, 50);
      if (!isCancelled && isReady) {
        setIsAuthCookieReady(true);
        return;
      }

      if (!isCancelled) {
        logger.auth.warn('loginAccessToken cookie still missing after wait window; protected routes remain gated');
      }
    };

    void ensureAuthCookie();

    return () => {
      isCancelled = true;
    };
  }, [isAuthenticated, accounts, instance]);

  useEffect(() => {
    logger.app.debug('Authentication state changed:', {
      accountsCount: accounts.length,
      isAuthenticated,
      inProgress,
      currentPath: window.location.pathname
    });
  }, [accounts.length, isAuthenticated, inProgress]);

  // When an unauthenticated user lands directly on an export-download URL
  // (e.g. from an emailed link), save the path so we can restore it after
  // the MSAL login redirect brings them back to the app root.
  useEffect(() => {
    if (!isAuthenticated && window.location.pathname.startsWith('/export-download/')) {
      sessionStorage.setItem(EXPORT_DOWNLOAD_REDIRECT_KEY, window.location.pathname);
      logger.app.debug('Saved export-download return path:', window.location.pathname);
    }
  }, []);

  // After a successful login, redirect back to any saved export-download path.
  useEffect(() => {
    if (isAuthenticated && isAuthCookieReady) {
      const savedPath = sessionStorage.getItem(EXPORT_DOWNLOAD_REDIRECT_KEY);
      if (savedPath !== null) {
        sessionStorage.removeItem(EXPORT_DOWNLOAD_REDIRECT_KEY);
        logger.app.debug('Restoring export-download path after login:', savedPath);
        window.location.replace(savedPath);
      }
    }
  }, [isAuthenticated, isAuthCookieReady]);

  useEffect(() => {
    if (!isEmpty(type) && !isEmpty(message)) setIsNotificationShowing(true);
  }, [type, message]);

  logger.app.debug('THEME ==> ', theme);
  logger.app.debug('IS LOADINGS ==> ', isLoading);
  logger.app.debug('LOADINGS ==> ', loadings);

  // Show Error, Warning or Success messages.
  useEffect(() => {
    if (isNotificationShowing)
      setTimeout(() => {
        setIsNotificationShowing(false);
        setType('');
        setMessage('');
      }, 3000);
  }, [isNotificationShowing]);

  useEffect(() => {
    if (isLogin && ((user.userRedirectURL ?? "").length > 0)) {
      // DHL users (*.@dhl.com) are never auto-redirected between apps.
      // Check both the isDhl flag and the email domain as a fallback,
      // because some backends (e.g. LifeTrack) may not return isDhl.
      if (user.isDhl || validateDHLEmailDomain(user.email ?? '')) {
        logger.app.debug('DHL user detected - skipping redirect');
        return;
      }
      if (window.location.origin !== user.userRedirectURL)  {
        if (isDevelopment) {
          logger.app.debug(`Redirect to ${user.userRedirectURL} ignored in development mode`);
        } else {
          window.location.replace(`${user.userRedirectURL}/login`);
        }
      }
    }
  }, [isLogin])

  // Show loading while MSAL is initializing
  if (inProgress === 'startup') {
    logger.app.debug('MSAL is starting up, showing loading screen');
    return <div>Loading authentication...</div>;
  }

  if (isAuthenticated && !isAuthCookieReady) {
    logger.app.debug('Authenticated user waiting for loginAccessToken cookie before protected route load');
    return <div>Loading authentication...</div>;
  }

  const content = (): React.JSX.Element => (
    <BrowserRouter>
      <ScrollToTop />
      <AppWrapper theme={theme} impersonated={user?.impersonated ?? false}>
        <Header />
        <BottomWrapper theme={theme}>
          <MainContentWrapper role="main">
            <ContentWrapper theme={theme}>
              <FaroRoutes>
                {!isAuthenticated ? (
                  // PUBLIC ROUTES - Only load public bundle (limited /rafadmin calls)
                  <>
                    {logger.app.debug('Rendering PUBLIC routes')}
                    <Route path="/" element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.PublicHome} />)} />
                    <Route path={PATHS.HOME_AUTOMATIC_LOGIN} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.PublicHome} />)}/>
                    <Route path={PATHS.PRIVACY_NOTICE} element={<LazyRouteLoader name={MSCRoutes.PrivacyPage} />} />
                    {/* Export-download: show login screen; path is saved to sessionStorage above and restored after auth */}
                    <Route path={PATHS.EXPORT_DOWNLOAD} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.PublicHome} />)} />
                    <Route path={PATHS.ERROR} element={withAdobeAnalytics(<Error />)} />
                    <Route path={PATHS.INACTIVE} element={withAdobeAnalytics(<Error />)} />
                    <Route path={PATHS.EXPIRED} element={withAdobeAnalytics(<Error />)} />
                    <Route path={PATHS.NOT_FOUND} element={withAdobeAnalytics(<Error />)} />
                  </>
                  ) : (
                  // PROTECTED ROUTES - Load protected bundle (with full /rafadmin access)
                  <>
                    {logger.app.debug('Rendering PROTECTED routes')}
                    <Route path="/" element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.PrivateHome} />)} />
                    <Route path={PATHS.HOME_AUTOMATIC_LOGIN} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.PrivateHome} />)}/>
                    <Route path={PATHS.PRIVACY_NOTICE} element={<LazyRouteLoader name={MSCRoutes.PrivacyPage} />} />
                    <Route path={PATHS.UPLOAD_USER} element={<LazyRouteLoader name={MSCRoutes.UploadUser} />} />
                    <Route path={PATHS.EXTERNAL_USER} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.ExternalUser} />)} />
                    <Route path={PATHS.CUSTOMERS} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.CoList} />)} />
                    <Route path={PATHS.CUSTOMER_DETAIL} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.CoPage} />)} />
                    <Route path={PATHS.CUSTOMER_ADD} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.CoPage} />)} />
                    <Route path={PATHS.PROFILE} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.ProfilePage} />)} />
                    <Route path={PATHS.LOGOUT} element={withAdobeAnalytics(<Error />)} />
                    <Route path={PATHS.APP_BRANDING_LIST} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.AppBrandings} />)} />
                    <Route
                      path={PATHS.APP_BRANDING_OBJECT_DETAIL}
                      element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.AppBrandingsPage} />)}
                    />
                    <Route
                      path={PATHS.APP_BRANDING_OBJECT_ADD}
                      element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.AppBrandingsPage} />)}
                    />
                    <Route path={PATHS.BRANDING_LIST} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.Brandings} />)} />
                    <Route path={PATHS.APP_DETAILS} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.AppListPage} />)} />
                    <Route path={PATHS.CATEGORIES} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.Categories} />)} />
                    <Route path={PATHS.CATEGORIES_DETAIL_DETAIL} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.Category} />)} />
                    <Route path={PATHS.CATEGORIES_ADD} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.Category} />)} />
                    <Route
                      path={PATHS.BRANDING_OBJECT_DETAIL_DETAIL}
                      element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.Branding} />)}
                    />
                    <Route path={PATHS.BRANDING_OBJECT_ADD} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.Branding} />)} />
                    <Route path={PATHS.NEWS_LIST} element={<LazyRouteLoader name={MSCRoutes.NewsListPage} />} />
                    <Route path={PATHS.NEWS_ADD} element={<LazyRouteLoader name={MSCRoutes.NewsListUpdatePage} />} />
                    <Route path={PATHS.NEWS_EDIT} element={<LazyRouteLoader name={MSCRoutes.NewsListUpdatePage} />} />
                    <Route path={PATHS.USERS} element={withAdobeAnalytics(<LazyRouteLoader name={MSCRoutes.UsersPage} />)} />
                    <Route path={PATHS.EXPORT_DOWNLOAD} element={<LazyRouteLoader name={MSCRoutes.ExportDownload} />} />
                    <Route path={PATHS.ERROR} element={withAdobeAnalytics(<Error />)} />
                    <Route path={PATHS.INACTIVE} element={withAdobeAnalytics(<Error />)} />
                    <Route path={PATHS.EXPIRED} element={withAdobeAnalytics(<Error />)} />
                    <Route path={PATHS.NOT_FOUND} element={withAdobeAnalytics(<Error />)} />
                  </>
                )}
              </FaroRoutes>
            </ContentWrapper>
          </MainContentWrapper>

          <FooterWrapper role="contentinfo" theme={theme}>
            <DhlFooter versionData={versionData} metaData={metaData} copyright="App.footer.rights" />
          </FooterWrapper>
        </BottomWrapper>

        <DhlModal
          shouldShowCloseButton={false}
          isOpen={isUserLoading || isLoading}
          trapFocus={false}
          dataId="my-loading-dialog"
          dataClassName="modelView"
        >
          <DhlLoader size="sm" />
        </DhlModal>

        {isNotificationShowing && <DhlSnackbar type={type} message={message} />}
      </AppWrapper>
    </BrowserRouter>
  );

  if (user?.impersonated === true) {
    return (
      <ImpersonationWrapper theme={theme} impersonated={user?.impersonated ?? false}>
        {content()}
      </ImpersonationWrapper>
    );
  } else {
    return content();
  }
}

export default App;
